import type { ValidationIssue } from "../types/SemanticTypes";
import { wordService } from "../services/wordService";

export interface ArticleValidationOptions {
  onlyUntaggedCandidates?: boolean;
}

export async function validateArticle(options: ArticleValidationOptions = {}): Promise<ValidationIssue[]> {
  const controls = await wordService.readSemanticControls();
  const paragraphs = await wordService.readParagraphs();
  const semanticParagraphs = await wordService.readSemanticParagraphs();
  const issues: ValidationIssue[] = [];
  const hasType = (type: string) => controls.some((item) => item.metadata.type === type);

  if (!options.onlyUntaggedCandidates) {
    if (!hasType("title")) issues.push(issue("missing-title", "error", "Missing tagged title."));
    if (!hasType("abstract")) issues.push(issue("missing-abstract", "error", "Missing tagged abstract."));
    if (!hasType("keywords")) issues.push(issue("missing-keywords", "warning", "Missing tagged keywords."));
    if (!hasType("references")) issues.push(issue("missing-references", "warning", "Missing tagged references."));
    for (const type of ["figure", "table"] as const) {
      const parents = controls.filter((item) => item.metadata.type === type);
      const captionType = `${type}_caption`;
      for (const parent of parents) {
        const hasCaption = controls.some((item) => item.metadata.type === captionType && item.metadata.parent === parent.metadata.id);
        if (!hasCaption) {
          issues.push(issue(`missing-${type}-caption`, "warning", `Missing caption for ${parent.metadata.id}.`, undefined, { targetId: parent.metadata.id ?? "" }));
        }
      }
    }
  }

  const taggedText = new Set(semanticParagraphs.map((item) => item.trim()).filter(Boolean));
  paragraphs
    .filter((paragraph) => paragraph.length > 20 && !taggedText.has(paragraph))
    .filter((paragraph) => /^[A-Z][A-Za-z0-9 ,:;-]{10,90}$/.test(paragraph))
    .forEach((paragraph, index) =>
      issues.push(issue("possible-untagged-heading", "info", "Large heading-like paragraph is not semantically tagged.", `Paragraph ${index + 1}`, { text: paragraph }))
    );

  return issues;
}

function issue(
  code: string,
  severity: ValidationIssue["severity"],
  message: string,
  location?: string,
  metadata?: ValidationIssue["metadata"]
): ValidationIssue {
  return { code, severity, message, location, metadata };
}
